import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { downloadDocuments, uploadDocument } from "../Helper/DocumentsHelper";
import UploadFileBtn from "../Helper/UploadFileBtn/UploadFileBtn";
import { downloadShipmentsToExcel } from "../Cases/GeneralCases/CaseUtils";
import { DocumentsTypes } from "../Enum/DocumentsType";
import DataGridTamplet from "../Helper/TableTemplet/DataGridTamplet";
import { GridRenderCellParams } from "@mui/x-data-grid";
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { Box, Button, CircularProgress, Backdrop, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Snackbar, Alert, IconButton } from "@mui/material";
import { useNavigate } from "react-router-dom";
import "./Shipment.scss";
import { RootState } from "../../Redux/store";
import { fetchAndStorePackingList } from "../../Services/http/PackingListService";
import { createPackingListZip } from "../packing_list/ZipHelper";
import { gatherDataForPdf, preparePackingListData } from "../packing_list/PackingListUtils";
import CaseService from "../../Services/http/CaseService";
import { useTranslation } from "react-i18next";
import { AppDispatch } from "../../Redux/store";

type ToastState = {
  open: boolean;
  message: string;
  severity: "success" | "error";
};

const Shipments = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { t } = useTranslation("shipments");

  const [isDownloadingZip, setDownloadingZip] = useState(false);
  const [closedShipmentsWithShortages, setClosedShipmentsWithShortages] = useState([]);
  const [isLoadingShipments, setIsLoadingShipments] = useState(true);
  const [deletingShipmentIds, setDeletingShipmentIds] = useState<Set<string>>(new Set());
  const [deleteConfirmDialog, setDeleteConfirmDialog] = useState<{ open: boolean; shipmentId?: string; }>({ open: false });
  const [toast, setToast] = useState<ToastState>({
    open: false, message: "", severity: "success"
  });
  const defaultAccount = useSelector((state: any) => state.account.default_account) as { id?: string };
  const packingList = useSelector((state: RootState) => state.packingList);

  // Fetch closed shipments with shortages and update state
  const fetchClosedShipmentsWithShortages = async (accountId) => {
    setIsLoadingShipments(true);
    try {
      const shipmentsResponse = await CaseService.getClosedShipmentsWithShortages(accountId);
      if (shipmentsResponse) {
        setClosedShipmentsWithShortages(shipmentsResponse);
      } else {
        console.error("No shipments found or received an invalid response:", shipmentsResponse);
      }
    } catch (fetchError) {
      console.error("Error occurred while fetching shipments:", fetchError);
    } finally {
      setIsLoadingShipments(false);
    }
  };

  // Load packing list once when the component mounts
  useEffect(() => {
    const loadPackingList = async () => {
      if (defaultAccount && defaultAccount.id) {
        await dispatch(fetchAndStorePackingList(defaultAccount.id));
      }
    };
    loadPackingList();
  }, [dispatch, defaultAccount]);

  // Call fetchClosedShipmentsWithShortages in useEffect
  useEffect(() => {
    if (defaultAccount && defaultAccount.id) {
      fetchClosedShipmentsWithShortages(defaultAccount.id);
    }
  }, [defaultAccount]);

  // Handle ZIP download process for all packing lists
  const handleDownloadAllPackingListsAsZip = async () => {
    setDownloadingZip(true);
    try {
      // Use the preparePackingListData function to get the packing list data
      const packingListData = await preparePackingListData(closedShipmentsWithShortages, packingList);
      // Create ZIP with all packing list PDFs
      await createPackingListZip(packingListData);
    } catch (error) {
      alert(t("error_during_download"));
      console.error(error);
    } finally {
      setDownloadingZip(false);
    }
  };

  // Navigate to individual packing list for a specific shipment ID
  const navigateToPackingListForShipment = async (shipment_id: string) => {
    const packingListData = await gatherDataForPdf(shipment_id, packingList);
    navigate(`/layout/packing-list-for-shipment/${shipment_id}`, {
      state: { packingListData },
    });
  };
  const showToast = (message: string, severity: ToastState["severity"] = "success") => {
    setToast({ open: true, message, severity });
  };

  // Handle delete confirmation dialog open
  const handleOpenDeleteDialog = (shipmentId: string) => {
    setDeleteConfirmDialog({ open: true, shipmentId });
  };

  // Handle delete confirmation dialog close
  const handleCloseDeleteDialog = () => {
    setDeleteConfirmDialog({ open: false });
  };

  // Handle delete with optimistic UI
  const handleDeleteShipment = async () => {
    const { shipmentId } = deleteConfirmDialog;
    if (!shipmentId || !defaultAccount?.id) return;

    setDeletingShipmentIds(prev => new Set(prev).add(shipmentId));

    try {
      await CaseService.deleteShipment(Number(defaultAccount.id), shipmentId);
      // remove locally AFTER success (no refetch)
      setClosedShipmentsWithShortages(prev =>
        prev.filter((row: any) => row.shipment_id !== shipmentId)
      );

      showToast(t("shipment_deleted_successfully"), "success");
    } catch (e) {
      console.error(e);
      showToast(t("error_deleting_shipment"), "error");
    } finally {
      setDeletingShipmentIds(prev => {
        const ns = new Set(prev);
        ns.delete(shipmentId);
        return ns;
      });
      handleCloseDeleteDialog();
    }
  };


  // Format date helper function
  const formatExpirationDate = (dateString: string | null | undefined): string => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) return "-";
    return date.toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" });
  };

  // Column definitions for the data grid
  const columns = [
    { field: "shipment_id", headerName: t("shipment_id"), flex: 1 },
    {
      field: "expires_at",
      headerName: t("expires_at"),
      flex: 1,
      renderCell: (params: GridRenderCellParams) => (
        <span>{formatExpirationDate(params.row.expires_at)}</span>
      ),
      sortable: true,
    },
    {
      field: "document_upload",
      headerName: t("document_upload"),
      flex: 1,
      renderCell: (params: GridRenderCellParams) => (
        <Box sx={{ transform: "scale(0.9)" }}>
          <UploadFileBtn
            id={params.row.id}
            onSubmit={importDocument}
            multiple={true}
            index={params.row.id}
            title=""
            fileType={"imageAndPdf"}
            autoUpload={true}
            buttonText={t("upload")}
          />
        </Box>
      ),
    },
    {
      field: "document_download",
      headerName: t("download_files"),
      flex: 1,
      renderCell: (params: GridRenderCellParams) => {
        const count = params.row?.documents?.length ?? 0;
        const disabled = count === 0;

        return (
          <Box display="flex" alignItems="center" gap={1}>
            <IconButton
              size="small"
              onClick={() => download(params)}
              disabled={disabled}
              aria-label={t("download_files")}
            >
              <DownloadOutlinedIcon />
            </IconButton>

            <Box
              component="span"
              style={{
                fontSize: 12,
                opacity: disabled ? 0.5 : 1,
                whiteSpace: "nowrap",
              }}
            >
              {count} {t("files")}
            </Box>
          </Box>
        );
      }
    },
    {
      field: "packing_list",
      headerName: t("packing_list"),
      flex: 1,
      width: 160,
      renderCell: (params: GridRenderCellParams) => (
        <Button
          variant="contained"
          color="primary"
          onClick={() => navigateToPackingListForShipment(params.row.shipment_id)}
        >
          {t("packing_list")}
        </Button>
      ),
    },
    {
      field: "actions",
      sortable: false,
      filterable: false,
      headerName: t("actions"),
      flex: 1,
      width: 100,
      renderCell: (params: GridRenderCellParams) => (
        <IconButton
          aria-label={t("delete_shipment")}
          onClick={() => handleOpenDeleteDialog(params.row.shipment_id)}
          disabled={deletingShipmentIds.has(params.row.shipment_id)}
        >
          <DeleteOutlineIcon sx={{ color: "#d32f2f", fontSize: "1.2rem" }} />        </IconButton>
      ),
    }
  ];

  const download = async (item) => {
    try {
      const count = item?.row?.documents?.length ?? 0;
      if (count > 0) {
        await downloadDocuments(dispatch, defaultAccount.id, 'shipment', item.id, null);
      } else {
        alert(t("no_documents_found"));}
    } catch (error) {
      alert(error);
    }
  };

  const importDocument = async (file, id = null, index) => {
    const docs = await uploadDocument(
      dispatch,
      file,
      DocumentsTypes.SHIPMENT,
      defaultAccount.id,
      id,
      index
    );
    setClosedShipmentsWithShortages(prev =>
      prev.map((row: any) =>  row.id === id? { ...row, documents: docs }: row)
    );
  };

  const downloadShipments = async () => {
    try {
      await downloadShipmentsToExcel(dispatch, defaultAccount.id);
    } catch (error) {
      alert(error);
    }
  };

  // Navigate to the "Create Packing List" page
  const navigateToPackingList = () => {
    navigate("/layout/packing-list-form");
  };

  return (
    <div className="shipment">
      <h2>{t("inbound_shipments")}</h2>
      <Button
        variant="contained"
        color="primary"
        sx={{ mb: 2 }}
        onClick={navigateToPackingList}
      >
        {t("create_packing_list")}
      </Button>
      {isLoadingShipments ? (
        <Box display="flex" justifyContent="center" alignItems="center" mt={3}>
          <CircularProgress />
        </Box>
      ) : (
        <DataGridTamplet columns={columns} rows={closedShipmentsWithShortages} />
      )}

      <Box className="action-buttons">
        <Button
          className="blue-btn"
          size="large"
          onClick={downloadShipments}
          sx={{ marginRight: 2 }}
        >
          {t("export_discrepancies")}
        </Button>
        <Button
          variant="contained"
          className="blue-btn"
          onClick={handleDownloadAllPackingListsAsZip}
          disabled={isDownloadingZip}
        >
          {t("download_all_packing_lists")}
        </Button>
      </Box>

      {/* Full-screen loading backdrop for ZIP download */}
      <Backdrop
        sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={isDownloadingZip}
      >
        <CircularProgress color="inherit" />
        <Box ml={2}>{t("generating_zip")}</Box>
      </Backdrop>

      {/* Delete confirmation dialog */}
      <Dialog
        open={deleteConfirmDialog.open}
        onClose={handleCloseDeleteDialog}
      >
        <DialogTitle>{t("confirm_delete_shipment") || "Delete Shipment"}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t("delete_shipment_confirmation") || "Are you sure you want to delete this shipment? This action cannot be undone."}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog} color="primary">
            {t("cancel") || "Cancel"}
          </Button>
          <Button
            onClick={handleDeleteShipment}
            color="error"
            variant="contained"
            disabled={deletingShipmentIds.has(deleteConfirmDialog.shipmentId || "")}
          >
            {deletingShipmentIds.has(deleteConfirmDialog.shipmentId || "") ? (
              <CircularProgress size={20} sx={{ mr: 1 }} />
            ) : null}
            {t("delete") || "Delete"}
          </Button>
        </DialogActions>
      </Dialog>
      <Snackbar
        open={toast.open}
        autoHideDuration={4000}
        onClose={(_, reason) => {
          if (reason === "clickaway") return;
          setToast(prev => ({ ...prev, open: false }));
        }}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setToast(prev => ({ ...prev, open: false }))}
          severity={toast.severity}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {toast.message}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default Shipments;
